import { Badge, Button, Dropdown, Modal, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DownIcon from '../../../../../assets/icons/dropdown-down-arrow.svg';
import GenAI from '../../../../../assets/icons/GenAi/gen-ai-search-plus-icon.svg';
import Mail from '../../../../../assets/icons/mail.svg';
import Notification from '../../../../../assets/icons/notifications.svg';
import DropdownMenu from '../../../../ui/DropdownMenu/DropdownMenu';
import NotificationDrawer from '../../../../modals/NotificationDrawer/NotificationDrawer';
import { IAuditHistoryPayload, IProjectDetails } from '../../../../../libs/models/DashboardModels';
import {
  IPinAccountPayload,
  ISelectAccountPayload,
  ISelectProjectPayload,
  ProjectStatus,
} from '../../../../../libs/models/OverviewModels';
import { storage } from '../../../../../libs/services/LocalStorage';
import { DashboardActions } from '../../../../../libs/store/actions/DashboardActions';
import { NotificationActions } from '../../../../../libs/store/actions/NotificationActions';
import { OverviewActions } from '../../../../../libs/store/actions/OverviewActions';
import { SidebarActions } from '../../../../../libs/store/actions/SidebarActions';
import { IApplicationState, useAppSelector } from '../../../../../libs/store/reducers';
import CreateAccountPopup from '../../../../../components/business/CreateAccountPopup/CreateAccountPopup';
import EmailNotificationDrawer from './EmailNotificationDrawer/EmailNotificationDrawer';
import Chatbot from './ChatBot/Chatbot';
import { OverviewHeaderLabels as LABELS } from './OverviewConstants';
import './OverviewStyles.less';
import DrawerOverHead from './DrawerOverHead';
import SearchBox from './SearchBox';

const OverviewHeader = () => {
  const dispatch = useDispatch();
  const {
    selectedProject,
    selectedAccount,
    selectedAccountId,
    accountsProjectsMapping,
    project,
    account,
    logoutState,
    enabledDiscoverAssessItems,
    isVisible,
    landingZoneNotificationDot,
    isEmailNotificationVisible,
    emailNotificationDot,
    userDetails,
    recentSearchList,
  } = useAppSelector(
    ({
      user: { userDetails },
      sidebar: { logout },
      overview: {
        selectedProject,
        selectedAccount,
        selectedAccountId,
        selectedAssetType,
        selectedProjectId,
      },
      dashboard: { accountsProjectsMapping, enabledDiscoverAssessItems },
      notification: {
        isVisible,
        landingZoneNotificationDot,
        isEmailNotificationVisible,
        emailNotificationDot,
      },
      genAI: { recentSearchList },
    }: IApplicationState) => ({
      selectedProjectId,
      selectedProject,
      selectedAccount,
      selectedAccountId,
      accountsProjectsMapping,
      project:
        selectedProject && selectedProjectId
          ? { name: selectedProject, id: selectedProjectId }
          : undefined,
      account:
        selectedAccount && selectedAccountId && selectedAssetType
          ? { name: selectedAccount, id: selectedAccountId, assetType: selectedAssetType }
          : undefined,
      logoutState: logout,
      enabledDiscoverAssessItems,
      isVisible,
      landingZoneNotificationDot,
      isEmailNotificationVisible,
      emailNotificationDot,
      userDetails,
      recentSearchList,
    }),
  );

  const dispatcher = {
    setAccount: (data: ISelectAccountPayload) => dispatch(OverviewActions.selectAccount(data)),
    setProject: (data: ISelectProjectPayload) => dispatch(OverviewActions.selectProject(data)),
    saveAuditDetails: (data: IAuditHistoryPayload) =>
      dispatch(DashboardActions.saveAuditHistoryStart(data)),
    toggleLogout: (data: boolean) => dispatch(SidebarActions.toggleLogout(data)),
    showNotificationsDrawer: (data: boolean) =>
      dispatch(NotificationActions.toggleLandingZoneNotificationStart(data)),
    showEmailDrawer: (data: boolean) =>
      dispatch(NotificationActions.toggleEmailNotificationStart(data)),
    getAllAccountsStart: () => dispatch(DashboardActions.getAllAccountsStart()),
    pinAccountStart: (data: IPinAccountPayload) => dispatch(OverviewActions.pinAccountStart(data)),
  };

  const setAccount = (data: ISelectAccountPayload) => dispatcher.setAccount(data);
  const setProject = (data: ISelectProjectPayload) => dispatcher.setProject(data);
  const saveAuditDetails = (data: IAuditHistoryPayload) => dispatcher.saveAuditDetails(data);
  const toggleLogout = (data: boolean) => dispatcher.toggleLogout(data);
  const showNotificationsDrawer = (data: boolean) => dispatcher.showNotificationsDrawer(data);
  const showEmailDrawer = (data: boolean) => dispatcher.showEmailDrawer(data);
  const pinAccountStart = (data: IPinAccountPayload) => dispatcher.pinAccountStart(data);

  const accounts = accountsProjectsMapping.map(({ accountName, accountId, assetType, status }) => ({
    accountId,
    accountName,
    assetType,
    status,
  }));

  const [pinAccount, setPinAccount] = useState<boolean>(false);
  const [projects, setProjects] = useState<IProjectDetails[]>([]);
  const [isCreateAccountPopupOpen, setIsCreateAccountPopupOpen] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [query, setQuery] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [isGlobalSearch, setIsGlobalSearch] = useState<boolean>(true);
  const [cardContent, setCardContent] = useState<string>();

  useEffect(() => {
    // TODO: Use id instead of name
    const pinnedAccount = accountsProjectsMapping.filter(({ pinned }) => pinned === true);
    if (pinnedAccount.length === 1) {
      setAccount(pinnedAccount[0]);
      setPinAccount(true);
    }
    const account = accountsProjectsMapping.filter(
      ({ accountName }) => accountName === selectedAccount,
    );
    const projects = account.length ? account[0].projects : [];

    setProjects(projects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, accountsProjectsMapping]);

  useEffect(() => {
    dispatcher.getAllAccountsStart();
  }, []);

  const logout = () => {
    saveAuditDetails({
      dashboard: { project, account, enabledDiscoverAssessItems },
    });
  };

  if (logoutState) {
    toggleLogout(false);
    storage.clearAll();
    window.location.reload();
  }
  const handleDrawerOpener = () => {
    showNotificationsDrawer(true);
  };
  const handleEmailDrawerOpener = () => {
    showEmailDrawer(true);
  };
  const togglePin = () => {
    if (pinAccount) {
      pinAccountStart({
        accountId: selectedAccountId || '',
        action: 'unpin',
      });
    } else {
      pinAccountStart({
        accountId: selectedAccountId || '',
        action: 'pin',
      });
    }
    setPinAccount(!pinAccount);
  };
  const handleModalVisibleChange = () => {
    setShowModal(true);
  };

  return (
    <div className="overview-header-container-main">
      {/* <Button type="link" className="gen-AI-button" onClick={() => handleModalVisibleChange()}>
        <div className="gen-AI-elements">
          <img src={GenAI} alt="Dropdown Icon" />
          <Typography className="Gen-AI-text">{LABELS.genAiButton}</Typography>
        </div>
      </Button> */}

      <div className="overview-header-container">
        <Dropdown
          menu={{
            items: accounts
              .filter(({ status }) => status === ProjectStatus.ACTIVE)
              .map((account) => ({
                key: account.accountName,
                label: account.accountName,
                onClick: () => setAccount(account),
              })),
            selectedKeys: selectedAccount ? [selectedAccount] : [],
          }}
          trigger={['click']}
          disabled={pinAccount}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          className="overview-header-container-dropdown"
        >
          <Button className="select-account-button">
            <Typography className="select-button-text">
              {selectedAccount || LABELS.selectAccount}
            </Typography>
            {!pinAccount ? (
              <img src={DownIcon} className="select-button-down-icon" alt="Dropdown Icon" />
            ) : null}
          </Button>
        </Dropdown>
        {pinAccount ? (
          <img
            className="pin-account"
            alt="unpin account"
            title="unpin account"
            src={`${process.env.PUBLIC_URL}/unpin.svg`}
            onClick={togglePin}
          />
        ) : (
          <img
            className="pin-account"
            alt="pin account"
            title="pin account"
            src={`${process.env.PUBLIC_URL}/pin.svg`}
            onClick={togglePin}
          />
        )}
        <div className="divider-container">
          <div className="divider" />
        </div>
        <Dropdown
          menu={{
            items: projects
              .filter(({ status }) => status === ProjectStatus.ACTIVE)
              .map(({ projectName }) => ({
                key: projectName,
                label: projectName,
                onClick: () => setProject(projects.filter(({ projectName: name }) => name === projectName)[0]),
              })),
            selectedKeys: selectedProject ? [selectedProject] : [],
          }}
          trigger={['click']}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          className="overview-header-container-dropdown"
        >
          <Button className="select-project-button">
            <Typography className="select-button-text">
              {selectedProject || LABELS.selectProject}
            </Typography>
            <img src={DownIcon} className="select-button-down-icon" alt="Dropdown Icon" />
          </Button>
        </Dropdown>
      </div>
      {/* Condition to be change later */}
      {userDetails?.user_permissions.add_account && (
        <Button
          className="notification-icon-btn overview-message-btn"
          onClick={handleEmailDrawerOpener}
        >
          <Badge dot={emailNotificationDot} offset={[-3, 1]} className="notification-icon-badge">
            <img src={Mail} alt="notification-icon" />
          </Badge>
        </Button>
      )}
      <Button
        className="notification-icon-btn overview-notification-btn"
        onClick={handleDrawerOpener}
      >
        <Badge
          dot={landingZoneNotificationDot}
          offset={[-8, 8]}
          className="notification-icon-badge"
        >
          <img src={Notification} alt="notification-icon" />
        </Badge>
      </Button>
      {/* Code To Display the Notification Drawer */}
      {isCreateAccountPopupOpen && (
        <CreateAccountPopup onClose={() => setIsCreateAccountPopupOpen(false)} />
      )}
      {isVisible && <NotificationDrawer />}
      {isEmailNotificationVisible && <EmailNotificationDrawer isMobile />}
      <Modal
        open={showModal}
        width={1200}
        footer={null}
        closable
        closeIcon={<></>}
        onCancel={() => setShowModal(false)}
      >
        <SearchBox
          setShowModal={setShowModal}
          isDrawer={false}
          isKeywordSearch={true}
          query={query}
          setQuery={setQuery}
          setVisible={setVisible}
          setCardContent={setCardContent}
          setIsGlobalSearch={setIsGlobalSearch}
        />
      </Modal>
      <DrawerOverHead
        visible={visible}
        setVisible={setVisible}
        setQuery={setQuery}
        globalSearch={isGlobalSearch}
        query={query}
        cardContent={cardContent}
        setCardContent={setCardContent}
        setIsGlobalSearch={setIsGlobalSearch}
      />
      <Chatbot
        onClick={() => {
          setIsGlobalSearch(false);
          setVisible(true);
        }}
      />
    </div>
  );
};

export default OverviewHeader;
